import React, { useState, useEffect, useMemo } from 'react';
import DescriptionBanner from '../Components/DescriptionBanner';
import useGet from '../Hooks/useGet';
import useUserType from '../Hooks/useUserType';
import API_ENDPOINTS from '../Config/api';
import getCyberLogIcon from '../Utils/getCyberLogIcon';
import ClipLoader from 'react-spinners/ClipLoader';
import ListAziendeTable from '../Components/Table/ListAziendeTable1';
import ListTecniciTable from '../Components/Table/ListTecniciTable1'
import FornitorNonAttiviTable from '../Components/Table/FornitorNonAttiviTable'
import ListOrdiniTable from '../Components/Table/ListOrdiniTable';
import ResellerDashboard from '../Components/common/ResellerDashboard';



export default function MsspHome() {

  const { isTecnico, isReseller, isPartner, ordiniAbilitati } = useUserType();

  const [itemSidebarSelected, setItemSidebarSelected] = useState('Dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const loader = <ClipLoader />;

  const { fetchedData: listAziende, refetch: refetchListAziende } = useGet(API_ENDPOINTS.listaAziendeMSSP);
  const [aziende, setAziende] = useState()
  useEffect(() => { setAziende(listAziende) }, [listAziende])

  // Tecnici e Lead: fetch solo per MSSP tecnico (reseller/partner non li gestiscono)
  const { fetchedData: listTecnici, refetch: refetchlistTecnici } = useGet(
    isTecnico ? API_ENDPOINTS.listaTecniciMSSP : null
  );
  const [tecnici, setTecnici] = useState()
  useEffect(() => { setTecnici(listTecnici) }, [listTecnici])

  const { fetchedData: aziendeEnterpriseCaricate, refetch: refetchAziendeEnterpriseCaricate } = useGet(
    isTecnico ? API_ENDPOINTS.aziendeCaricate : null
  );
  const [aziendeEnterprise, setAziendeEnterprise] = useState()
  useEffect(() => { setAziendeEnterprise(aziendeEnterpriseCaricate) }, [aziendeEnterpriseCaricate])

  const { fetchedData: msspInfo } = useGet(API_ENDPOINTS.myInfoMSSP);
  const [mssp, setMssp] = useState()
  useEffect(() => { setMssp(msspInfo) }, [msspInfo])

  // Sempre fetch ordini - verrà usato solo se mssp.ordini_abilitati è true
  const { fetchedData: ordiniMSSP, refetch: refetchOrdiniMSSP } = useGet(API_ENDPOINTS.myOrdiniMSSP);
  const [ordini, setOrdini] = useState()
  useEffect(() => { 
    // Imposta ordini solo se l'MSSP ha ordini_abilitati
    if (mssp?.ordini_abilitati) {
      setOrdini(ordiniMSSP);
    } else {
      setOrdini(null);
    }
  }, [ordiniMSSP, mssp?.ordini_abilitati])

  // Sidebar condizionale in base al tipo utente
  const sidebarItems = useMemo(() => {
    const items = [{ label: 'Dashboard' }];

    // Tecnici e Lead visibili solo per MSSP tecnico (mssp/whitelabel)
    if (isTecnico) {
      items.push({ label: 'Tecnici' });
      items.push({ label: 'Lead' });
    }

    // Ordini visibili se ordini_abilitati (per tutti i tipi)
    if (ordiniAbilitati || mssp?.ordini_abilitati) {
      items.push({ label: 'Ordini' });
    }

    return items;
  }, [isTecnico, ordiniAbilitati, mssp?.ordini_abilitati]);

  const renderContent = () => {
    switch (itemSidebarSelected) {
      case 'Dashboard':
        // Reseller/Partner: dashboard KPI ridotta
        if (isReseller || isPartner) {
          return <ResellerDashboard ordini={ordini} aziende={aziende} mssp={mssp} />;
        }
        // MSSP tecnico: tabella aziende
        return aziende ? <ListAziendeTable data={aziende} setData={setAziende} refetchData={refetchListAziende} /> : loader
      case 'Tecnici':
        if (!isTecnico) return null;
        return tecnici ? <ListTecniciTable data={tecnici} setData={setTecnici} refetchData={refetchlistTecnici} /> : loader
      case 'Lead':
        if (!isTecnico) return null;
        return aziendeEnterprise ? <FornitorNonAttiviTable data={aziendeEnterprise} setData={setAziendeEnterprise} refetchData={refetchAziendeEnterpriseCaricate} /> : loader
      case 'Ordini':
        return ordini ? <ListOrdiniTable data={ordini} setData={setOrdini} refetchData={refetchOrdiniMSSP} userIsTecnico={isTecnico} /> : loader
      
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6 p-2 sm:p-4 lg:p-6 items-stretch">

      <div>
        <DescriptionBanner title="Pannello di controllo" description={isReseller ? 'Permette di visualizzare gli ordini inseriti dal reseller.' : 'Permette di avere una visione generale dei sub account gestiti e di creare nuovi sub account.'} />
      </div>

      <div className="flex flex-col lg:flex-row bg-gray-100 overflow-hidden relative">

        {/* Mobile menu button */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="lg:hidden flex items-center gap-2 bg-yellow-400 text-black font-semibold px-4 py-3 rounded-md mb-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          {itemSidebarSelected}
        </button>

        {/* Sidebar - Mobile: Overlay drawer, Desktop: Static */}
        <div
          className={`
            fixed lg:static inset-y-0 left-0 z-40 
            w-64 lg:w-60 flex-shrink-0 
            bg-white shadow flex flex-col p-4 
            overflow-auto
            transform transition-transform duration-300 ease-in-out
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            lg:h-auto h-screen lg:max-h-[36rem]
          `}
        >
          {/* Close button - mobile only */}
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden absolute top-4 right-4 text-gray-600 hover:text-gray-900"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {sidebarItems.map((item) => (
            <button
              key={item.label}
              onClick={() => {
                setItemSidebarSelected(item.label);
                setSidebarOpen(false);
              }}
              className={`
                flex gap-2 mb-4 items-center font-semibold w-full px-4 py-3 text-gray-700 rounded transition-colors whitespace-nowrap text-base min-h-[44px]
                ${itemSidebarSelected === item.label ? 'bg-yellow-400 text-black' : 'hover:bg-yellow-100 hover:text-black'}`}
            >
              {getCyberLogIcon('Consapevolezza', { className: 'w-5 h-5' })}
              {item.label}
            </button>
          ))}
        </div>

        {/* Backdrop overlay - mobile only */}
        {sidebarOpen && (
          <div
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Content */}
        <div className="flex-1 px-2 sm:px-4 lg:px-6 overflow-x-auto">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
